import React, { useEffect, useState } from 'react';
import { dashboardAPI } from '@/api/api';
import { Users, DollarSign, CheckCircle, XCircle, Clock } from 'lucide-react';

const ParentDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await dashboardAPI.getParentDashboard();
      setData(response.data);
    } catch (error) {
      console.error('Error fetching parent dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  const getAttendanceIcon = (status) => {
    switch(status) {
      case 'Present': return <CheckCircle className="text-green-500" size={20} />;
      case 'Absent': return <XCircle className="text-red-500" size={20} />;
      default: return <Clock className="text-gray-400" size={20} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Parent Dashboard</h1>
        <p className="text-gray-600 mt-1">Monitor your children's academic progress</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <StatCard
          icon={Users}
          title="Total Children"
          value={data?.summary?.total_children || 0}
          bgColor="bg-blue-500"
        />
        <StatCard
          icon={DollarSign}
          title="Total Fees Due"
          value={`₹${data?.summary?.total_fees_due?.toLocaleString('en-IN') || 0}`}
          bgColor="bg-red-500"
        />
      </div>

      {/* Children List */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">My Children</h2>
        
        {data?.children && data.children.length > 0 ? (
          <div className="space-y-4">
            {data.children.map((child) => (
              <div key={child.id} className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-semibold text-gray-800">{child.name}</h3>
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        {child.class}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Roll Number</p>
                        <p className="text-base font-semibold text-gray-800">{child.roll_number}</p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-gray-600">Today's Attendance:</p>
                        <div className="flex items-center gap-2">
                          {getAttendanceIcon(child.attendance_today)}
                          <span className={`font-semibold text-sm ${
                            child.attendance_today === 'Present' ? 'text-green-600' :
                            child.attendance_today === 'Absent' ? 'text-red-600' :
                            'text-gray-500'
                          }`}>
                            {child.attendance_today}
                          </span>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Fee Status</p>
                        <p className="text-sm font-semibold text-gray-800">
                          Paid: ₹{child.fees.paid?.toLocaleString('en-IN') || 0}
                        </p>
                        <p className="text-sm font-semibold text-red-600">
                          Balance: ₹{child.fees.balance?.toLocaleString('en-IN') || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Users className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500">No children records found</p>
            <p className="text-sm text-gray-400 mt-2">Please contact the school administrator</p>
          </div>
        )}
      </div>

      {/* Important Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-800 mb-2">📌 Important Information</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Attendance is marked daily by the class teacher</li>
          <li>• Fee payment can be made at the school office or online</li>
          <li>• Contact the administration for any queries</li>
        </ul>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, title, value, bgColor }) => (
  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-600 text-sm font-medium">{title}</p>
        <p className="text-3xl font-bold text-gray-800 mt-2">{value}</p>
      </div>
      <div className={`${bgColor} p-4 rounded-full`}>
        <Icon className="text-white" size={28} />
      </div>
    </div>
  </div>
);

export default ParentDashboard;
